import { apiFetch, apiFetchWithRetry } from '@/lib/apiFetch';
import { handleUnauthorized } from './UserManagmentServices';
import type { StudentExamTarget } from '@/types/registeredStudent';

const BASE = '/api/userManagement/v1/me/exam-targets';

export const MAX_EXAM_TARGETS = 3;

export class ExamTargetError extends Error {}

export function daysUntil(isoDate: string): number {
  const target = new Date(isoDate + 'T00:00:00');
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return Math.round((target.getTime() - now.getTime()) / 86400000);
}

export interface AddExamTargetInput {
  examId?: string;
  examName: string;
  catalogExamId?: string;
  examDate: string; // YYYY-MM-DD
  targetScore?: string;
  isResit?: boolean | null;
}

function authHeaders(accessToken: string): HeadersInit {
  return { 'Authorization': `Bearer ${accessToken}`, 'Content-Type': 'application/json' };
}

export async function listExamTargets(accessToken: string): Promise<StudentExamTarget[]> {
  const res = await apiFetchWithRetry(BASE, {
    method: 'GET',
    headers: authHeaders(accessToken),
  });
  if (res.status === 401) handleUnauthorized();
  if (!res.ok) throw new Error(`Failed to fetch exam targets: ${res.status}`);
  return res.json();
}

export async function addExamTarget(accessToken: string, input: AddExamTargetInput): Promise<StudentExamTarget> {
  const res = await apiFetch(BASE, {
    method: 'POST',
    headers: authHeaders(accessToken),
    body: JSON.stringify(input),
  });
  if (res.status === 401) handleUnauthorized();
  if (res.status === 409) throw new ExamTargetError('You can track up to 3 exam targets.');
  if (res.status === 400) {
    const body = await res.json().catch(() => ({}));
    const msg = body?.message ?? 'Exam date is required or must be in the future.';
    throw new ExamTargetError(msg);
  }
  if (!res.ok) throw new Error(`Failed to add exam target: ${res.status}`);
  return res.json();
}

export async function updateExamTarget(accessToken: string, id: string, patch: Partial<AddExamTargetInput>): Promise<StudentExamTarget> {
  const res = await apiFetch(`${BASE}/${id}`, {
    method: 'PUT',
    headers: authHeaders(accessToken),
    body: JSON.stringify(patch),
  });
  if (res.status === 401) handleUnauthorized();
  if (res.status === 404) throw new ExamTargetError('Exam target not found.');
  if (res.status === 400) throw new ExamTargetError('Exam date must be in the future.');
  if (!res.ok) throw new Error(`Failed to update exam target: ${res.status}`);
  return res.json();
}

export async function removeExamTarget(accessToken: string, id: string): Promise<void> {
  const res = await apiFetch(`${BASE}/${id}`, {
    method: 'DELETE',
    headers: authHeaders(accessToken),
  });
  if (res.status === 401) handleUnauthorized();
  if (res.status === 404) throw new ExamTargetError('Exam target not found.');
  if (!res.ok) throw new Error(`Failed to delete exam target: ${res.status}`);
}
